import React, {FC} from "react";
import MuiAlert, {AlertProps as MUIAlertProps} from "@material-ui/lab/Alert";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import {useTheme} from "@material-ui/core";
import useStyles from "./styles";

type AlertProps = MUIAlertProps & {
  fullWidth?: boolean;
  customIcon?: React.ReactNode;
};

const Alert: FC<AlertProps> = ({
  fullWidth,
  customIcon,
  severity = "info",
  ...props
}) => {
  const classes = useStyles();
  const theme = useTheme();

  // Force the icon colour per severity inline: when the Alert is rendered
  // inside greyed-out content (e.g. a DONE sub-task), the icon would otherwise
  // inherit that grey via `currentColor` instead of the severity colour.
  const iconColor = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  }[severity || "info"];

  const iconMapping: AlertProps["iconMapping"] = {
    success: customIcon || (
      <CheckCircleOutlineIcon
        fontSize="inherit"
        style={{color: iconColor, fill: iconColor}}
      />
    ),
    warning: customIcon || (
      <InfoOutlinedIcon
        fontSize="inherit"
        style={{color: iconColor, fill: iconColor}}
      />
    ),
    error: customIcon || (
      <ErrorOutlineIcon
        fontSize="inherit"
        style={{color: iconColor, fill: iconColor}}
      />
    ),
    info: customIcon || (
      <InfoOutlinedIcon
        fontSize="inherit"
        style={{color: iconColor, fill: iconColor}}
      />
    ),
  };

  const backgroundColor = {
    success: theme.palette.alertSuccess.main,
    error: theme.palette.alertError.main,
    warning: theme.palette.alertWarning.main,
    info: theme.palette.alertInfo.main,
  }[severity || "info"];

  return (
    <MuiAlert
      classes={{
        root: classes.root,
        icon: classes.icon,
        message: classes.message,
      }}
      severity={severity}
      style={{
        width: fullWidth ? "100%" : undefined,
        backgroundColor,
      }}
      iconMapping={iconMapping}
      {...props}
    />
  );
};

export default Alert;
